import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { AlertCircle } from "lucide-react";

export default function LimitsTab({ t }) {
  const { register, control, watch, formState: { errors } } = useFormContext();
  const currency = watch("currency");
  const feeType = watch("fee_type");

  return (
    <div className="space-y-4 mt-4">
      <div className="space-y-1">
        <Label>{t("currency")} *</Label>
        <Controller
          name="currency"
          control={control}
          rules={{ required: t("currencyRequired") || "Currency is required" }}
          render={({ field }) => (
            <Select
              value={field.value}
              onValueChange={field.onChange}
            >
              <SelectTrigger className={errors.currency ? "border-red-500 focus:ring-red-500/20" : ""}>
                <SelectValue placeholder={t("selectCurrency")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="THB">Thai Baht (THB)</SelectItem>
                <SelectItem value="PKR">Pakistani Rupee (PKR)</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
        {errors.currency && (
          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> {errors.currency.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>
            {t("minDeposit")} ({currency})
          </Label>
          <Input
            type="number"
            {...register("minDeposit", {
              valueAsNumber: true,
              min: { value: 0, message: t("minLimitError") || "Value must be at least 0" }
            })}
            className={errors.minDeposit ? "border-red-500 focus:ring-red-500/20" : ""}
          />
          {errors.minDeposit && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {errors.minDeposit.message}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <Label>
            {t("maxDeposit")} ({currency})
          </Label>
          <Input
            type="number"
            {...register("maxDeposit", {
              valueAsNumber: true,
              min: { value: 0, message: t("minLimitError") || "Value must be at least 0" }
            })}
            className={errors.maxDeposit ? "border-red-500 focus:ring-red-500/20" : ""}
          />
          {errors.maxDeposit && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {errors.maxDeposit.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>
            {t("minWithdrawal")} ({currency})
          </Label>
          <Input
            type="number"
            {...register("minWithdrawal", {
              valueAsNumber: true,
              min: { value: 0, message: t("minLimitError") || "Value must be at least 0" }
            })}
            className={errors.minWithdrawal ? "border-red-500 focus:ring-red-500/20" : ""}
          />
          {errors.minWithdrawal && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {errors.minWithdrawal.message}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <Label>
            {t("maxWithdrawal")} ({currency})
          </Label>
          <Input
            type="number"
            {...register("maxWithdrawal", {
              valueAsNumber: true,
              min: { value: 0, message: t("minLimitError") || "Value must be at least 0" }
            })}
            className={errors.maxWithdrawal ? "border-red-500 focus:ring-red-500/20" : ""}
          />
          {errors.maxWithdrawal && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {errors.maxWithdrawal.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>
            {t("dailyDepositLimit")} ({currency})
          </Label>
          <Input
            type="number"
            {...register("dailyDepositLimit", {
              valueAsNumber: true,
              min: { value: 0, message: t("minLimitError") || "Value must be at least 0" }
            })}
            className={errors.dailyDepositLimit ? "border-red-500 focus:ring-red-500/20" : ""}
          />
          {errors.dailyDepositLimit && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {errors.dailyDepositLimit.message}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <Label>
            {t("dailyWithdrawalLimit")} ({currency})
          </Label>
          <Input
            type="number"
            {...register("dailyWithdrawalLimit", {
              valueAsNumber: true,
              min: { value: 0, message: t("minLimitError") || "Value must be at least 0" }
            })}
            className={errors.dailyWithdrawalLimit ? "border-red-500 focus:ring-red-500/20" : ""}
          />
          {errors.dailyWithdrawalLimit && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {errors.dailyWithdrawalLimit.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>{t("feeType")}</Label>
          <Controller
            name="fee_type"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERCENTAGE">{t("percentage")}</SelectItem>
                  <SelectItem value="FIXED">{t("fixed")}</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="space-y-1">
          <Label>
            {t("feeValueLabel")} (
            {feeType === "PERCENTAGE" ? "%" : currency})
          </Label>
          <Input
            type="number"
            step="0.1"
            {...register("fee_rate", {
              valueAsNumber: true,
              min: { value: 0, message: t("minLimitError") || "Value must be at least 0" }
            })}
            className={errors.fee_rate ? "border-red-500 focus:ring-red-500/20" : ""}
          />
          {errors.fee_rate && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {errors.fee_rate.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
